import { useInView } from 'react-intersection-observer';
import SectionTitle from '../common/SectionTitle';
import { useCart } from '@context/CartContext';
import { getUserLanguage } from '../../utils/language';

const DriftTaxiSection = () => {
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { addToCart } = useCart();
  const language = getUserLanguage();

  const texts = {
    cs: {
      badge: 'Drift Taxi',
      title: 'Drift taxi s',
      highlightedText: 'mistrem ČR',
      description: 'Ponořte se do světa smyku a adrenalinu s Michalem Helebrandtem – mistrem ČR v driftu.',
      whatsWaitingTitle: 'Co vás čeká?',
      rideTitle: '10-ti minutová jízda',
      rideDesc: 'Jako spolujezdec na letišti nebo 1-2 kola na závodní dráze.',
      briefingTitle: 'Instruktáž',
      briefingDesc: 'Krátká instruktáž o bezpečnostních pravidlech a zajímavostech o driftu.',
      teamInteractionTitle: 'Interakce s týmem',
      teamInteractionDesc: 'Možnost vyzkoušet drobný servis vozidla a přátelský rozhovor s celým týmem.',
      giftTitle: 'Dárek na památku',
      giftDesc: 'Drobný týmový dárek jako vzpomínka na váš zážitek.',
      priceTitle: 'Cena zážitku',
      price: '5 000 Kč',
      validity: 'Platnost poukazu do 31.03.2026',
      validityNote: 'Uvedená platnost poukazu platí pouze pro nový nákup. Pakliže čerpáte kredit/univerzální poukaz, platnost Vašeho nového poukazu se řídí platností kreditu/univerzálního poukazu.',
      addToCart: 'Přidat do košíku',
      experienceTitle: 'Na co se budete těšit',
      experienceDesc1: 'Projedete se v závodním speciálu o síle 600 koní s mistrem ČR v driftu – Michalem Helebrandtem. Nejdříve se převléknete do speciální kombinézy, nasadíte helmu a připoutáte se pětibodovými pásy. Pak už vám nic nebrání si užít nezapomenutelnou jízdu plnou adrenalinu.',
      experienceDesc2: 'Při rezervaci zážitku si vyberete, zda chcete jízdu na letišti nebo závodní dráze. Po příchodu na místo konání předložíte svůj zážitkový poukaz. Čeká na vás skvělý zážitek v podobě jízdy řízené přetáčivým smykem. Po krátké instruktáži o bezpečnostních pravidlech a zajímavostí o driftu usedáte do sedadla spolujezdce a adrenalinová jízda začíná. Časová náročnost celého zážitku může být 1 - 2 hodiny.',
      locationsTitle: 'Lokality a dostupnost',
      location1: 'Celá ČR (dráha), Horní Libchava u České Lípy (letiště)',
      season: 'Jaro - podzim',
      persons: '1 osoba',
      duration: 'Cca 1 hodina (celý program)',
      whoCanJoinTitle: 'Kdo si může zážitek dopřát?',
      whoCanJoinDesc: 'Zážitek je vhodný pro osoby starší 18 let, případně mladší s podepsaným souhlasem rodičů. Není vhodný pro osoby se zdravotními problémy jako srdeční vady, těhotenství nebo pohybové obtíže.',
    },
    en: {
      badge: 'Drift Taxi',
      title: 'Drift Taxi with',
      highlightedText: 'Czech Champion',
      description: 'Dive into the world of skids and adrenaline with Michal Helebrandt – the Czech Republic drifting champion.',
      whatsWaitingTitle: 'What\'s Waiting for You?',
      rideTitle: '10-Minute Ride',
      rideDesc: 'As a passenger at an airfield or 1-2 laps on a racetrack.',
      briefingTitle: 'Briefing',
      briefingDesc: 'A short briefing on safety rules and interesting facts about drifting.',
      teamInteractionTitle: 'Team Interaction',
      teamInteractionDesc: 'A chance to try minor vehicle maintenance and have a friendly chat with the whole team.',
      giftTitle: 'Souvenir Gift',
      giftDesc: 'A small team gift as a memento of your experience.',
      priceTitle: 'Experience Price',
      price: '5,000 CZK',
      validity: 'Voucher valid until 31/03/2026',
      validityNote: 'The stated voucher validity applies only to new purchases. If you are using a credit/universal voucher, the validity of your new voucher is governed by the validity of the credit/universal voucher.',
      addToCart: 'Add to Cart',
      experienceTitle: 'What You\'ll Look Forward To',
      experienceDesc1: 'You\'ll ride in a 600-horsepower racing special with the Czech Republic drifting champion – Michal Helebrandt. First, you\'ll change into a special suit, put on a helmet, and buckle up with five-point harnesses. Then, nothing stands in the way of enjoying an unforgettable adrenaline-filled ride.',
      experienceDesc2: 'When booking the experience, you\'ll choose whether you want the ride at an airfield or on a racetrack. Upon arrival at the venue, you\'ll present your experience voucher. An amazing experience awaits you in the form of a ride controlled by oversteer. After a short briefing on safety rules and drifting facts, you\'ll take the passenger seat, and the adrenaline ride begins. The total duration of the experience may be 1–2 hours.',
      locationsTitle: 'Locations and Availability',
      location1: 'Across the Czech Republic (track), Horní Libchava near Česká Lípa (airfield)',
      season: 'Spring - Autumn',
      persons: '1 person',
      duration: 'Approx. 1 hour (entire program)',
      whoCanJoinTitle: 'Who Can Enjoy the Experience?',
      whoCanJoinDesc: 'The experience is suitable for individuals over 18, or younger with signed parental consent. It is not suitable for people with health issues such as heart conditions, pregnancy, or mobility problems.',
    },
  };

  const currentText = texts[language] || texts.en;

  const driftTaxiProduct = {
    id: 'drift-taxi-1',
    name: `${currentText.title} ${currentText.highlightedText}`,
    price: 5000,
    category: 'experience',
    translations: {
      cs: { name: 'Drift Taxi s mistrem ČR', description: '10-minutová jízda s Michalem Helebrandtem' },
      en: { name: 'Drift Taxi with Czech Champion', description: '10-minute ride with Michal Helebrandt' },
    },
  };

  const handleAddToCart = () => {
    addToCart(driftTaxiProduct);
  };

  return (
    <section id="drift-taxi" className="py-20 bg-dark-gray relative" ref={sectionRef}>
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/images/drift-taxi-bg.jpg"
          alt="Drift taxi background"
          className="w-full h-full object-cover opacity-10"
          loading="lazy"
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <SectionTitle
          badge={currentText.badge}
          title={currentText.title}
          highlightedText={currentText.highlightedText}
          description={currentText.description}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className={`drift-card ${inView ? 'animate-slide-in-left' : 'opacity-0'}`}>
            <div className="p-8">
              <h3 className="text-2xl font-title font-bold text-white mb-4">{currentText.whatsWaitingTitle}</h3>
              <ul className="space-y-6">
                {[
                  { title: currentText.rideTitle, desc: currentText.rideDesc },
                  { title: currentText.briefingTitle, desc: currentText.briefingDesc },
                  { title: currentText.teamInteractionTitle, desc: currentText.teamInteractionDesc },
                  { title: currentText.giftTitle, desc: currentText.giftDesc },
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="mt-1 mr-4 flex-shrink-0 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-xs text-white">
                      <i className="fas fa-check" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{item.title}</h4>
                      <p className="text-gray-400 text-sm mt-1">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <div className="bg-black/30 p-5 rounded-lg mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-white">{currentText.priceTitle}</h4>
                    <span className="price-tag">{currentText.price}</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-2">{currentText.validity}</p>
                  <p className="text-gray-500 text-xs italic">{currentText.validityNote}</p>
                </div>
                <button
                  onClick={handleAddToCart}
                  className="btn-drift w-full flex justify-center items-center"
                  aria-label={currentText.addToCart}
                >
                  <i className="fas fa-shopping-cart text-xs mr-2" />
                  {currentText.addToCart}
                </button>
              </div>
            </div>
          </div>

          <div className={`space-y-8 ${inView ? 'animate-slide-in-right' : 'opacity-0'}`}>
            <div className="drift-card p-6">
              <h3 className="text-xl font-title font-bold text-white mb-4">{currentText.experienceTitle}</h3>
              <p className="text-gray-400 mb-4">{currentText.experienceDesc1}</p>
              <h4 className="font-medium text-white mb-2">
                {language === 'cs' ? 'Co se bude dít?' : 'What Will Happen?'}
              </h4>
              <p className="text-gray-400">{currentText.experienceDesc2}</p>
            </div>

            <div className="drift-card p-6">
              <h3 className="text-xl font-title font-bold text-white mb-4">{currentText.locationsTitle}</h3>
              <ul className="text-gray-400 space-y-3 mb-4">
                {[
                  { icon: 'fa-map-marker-alt', text: currentText.location1 },
                  { icon: 'fa-calendar', text: currentText.season },
                  { icon: 'fa-user', text: currentText.persons },
                  { icon: 'fa-clock', text: currentText.duration },
                ].map((item, index) => (
                  <li key={index} className="flex items-center">
                    <i className={`fas ${item.icon} text-red-500 mr-2`} />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
              <h4 className="font-medium text-white mb-2">{currentText.whoCanJoinTitle}</h4>
              <p className="text-gray-400">{currentText.whoCanJoinDesc}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DriftTaxiSection;